/**
 * CutestPluckEntryPage — public single-entry detail view.
 *
 * Public route at `/cutest-pluck/entries/:entryId`. Fetches
 * `GET /api/contest/entries/{entryId}` — the endpoint 404s for
 * anything but `status="approved"`, so the "not found" state
 * covers pending/rejected/nonexistent uniformly (no info leak).
 *
 * Dynamic OG tags via `react-helmet-async`:
 *   * `og:title`   = `"Vote for {dog_name} — Cutest Pluck on the Block"`
 *   * `og:description` = caption if present, else generic prompt
 *   * `og:image`   = entry's photo_url resolved to an absolute URL
 *                    (relative `/api/uploads/...` won't scrape well
 *                    off the origin)
 *   * `og:url`     = absolute detail URL
 *   * `og:type`    = `article`
 *
 * Share button uses `navigator.clipboard.writeText` — native, no
 * third-party dependency. Falls back gracefully with a toast if
 * clipboard access is denied by the browser.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicPageShell from "@/components/PublicPageShell";
import PoliteStatus from "@/components/a11y/PoliteStatus";
import {
  ArrowLeft,
  Heart,
  Check,
  Loader2,
  Share2,
  Trophy,
  ImageOff,
} from "lucide-react";

function absoluteUrl(path) {
  if (typeof window === "undefined") return path;
  if (!path) return "";
  if (/^https?:/i.test(path)) return path;
  return `${window.location.origin}${path}`;
}

function formatVoteCount(n) {
  return n === 1 ? "1 vote" : `${n} votes`;
}

export default function CutestPluckEntryPage() {
  const { entryId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | ok | notfound | error
  const [pending, setPending] = useState(false);
  const [isMyEntry, setIsMyEntry] = useState(false);

  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const resp = await api.get(`/contest/entries/${entryId}`);
      setEntry(resp.data);
      setPhase("ok");
      if (user) {
        try {
          const meEntry = await api.get("/contest/entries/me");
          setIsMyEntry(meEntry.data?.id === entryId);
        } catch {
          setIsMyEntry(false);
        }
      } else {
        setIsMyEntry(false);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setPhase("notfound");
      } else {
        setPhase("error");
      }
    }
  }, [entryId, user]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleVote() {
    if (!user) {
      navigate(`/login?next=/cutest-pluck/entries/${entryId}`, {
        replace: false,
      });
      return;
    }
    const alreadyVoted = entry.has_voted_by_me === true;
    // Optimistic UI.
    setEntry((cur) => ({
      ...cur,
      vote_count: cur.vote_count + (alreadyVoted ? -1 : 1),
      has_voted_by_me: !alreadyVoted,
    }));
    setPending(true);
    try {
      if (alreadyVoted) {
        await api.delete(`/contest/entries/${entryId}/vote`);
      } else {
        await api.post(`/contest/entries/${entryId}/vote`);
      }
    } catch (err) {
      // Roll back.
      setEntry((cur) => ({
        ...cur,
        vote_count: cur.vote_count + (alreadyVoted ? 1 : -1),
        has_voted_by_me: alreadyVoted,
      }));
      const detail = err?.response?.data?.detail;
      const errCode = typeof detail === "object" ? detail?.error : null;
      if (err?.response?.status === 429 || errCode === "rate_limited") {
        toast.error("Slow down! Your vote is safe — try again in a moment.");
      } else {
        toast.error(
          formatApiErrorDetail(detail) || "Something went wrong. Please try again.",
        );
      }
    } finally {
      setPending(false);
    }
  }

  async function handleShare() {
    const url = absoluteUrl(`/cutest-pluck/entries/${entryId}`);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied!");
    } catch {
      toast.error("Couldn't copy — please copy the URL manually.");
    }
  }

  const canonicalUrl = absoluteUrl(`/cutest-pluck/entries/${entryId}`);
  const dogName = entry?.dog_name || "";
  const ogTitle = dogName
    ? `Vote for ${dogName} — Cutest Pluck on the Block`
    : "Cutest Pluck entry — PooPluckers";
  const ogDescription =
    entry?.caption ||
    (dogName
      ? `Vote for ${dogName} in the PooPluckers Cutest Pluck contest.`
      : "Vote in the PooPluckers Cutest Pluck contest.");
  const ogImage = entry?.photo_url ? absoluteUrl(entry.photo_url) : "";

  return (
    <PublicPageShell mainClass="px-6 py-12">
      {phase === "ok" && entry && (
        <Helmet>
          <title>{ogTitle}</title>
          <meta name="description" content={ogDescription} />
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDescription} />
          {ogImage && <meta property="og:image" content={ogImage} />}
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="article" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={ogTitle} />
          <meta name="twitter:description" content={ogDescription} />
          {ogImage && <meta name="twitter:image" content={ogImage} />}
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
      )}

      <div className="mx-auto max-w-3xl">
        <Link
          to="/cutest-pluck"
          className="inline-flex items-center gap-1 text-sm font-semibold text-brand-orange hover:underline"
          data-testid="cutest-pluck-entry-back"
        >
          <ArrowLeft className="h-4 w-4" />
          See all entries
        </Link>

        {phase === "loading" && (
          <div
            className="mt-6 flex items-center justify-center rounded-2xl border border-border bg-pp-surface p-10"
            data-testid="cutest-pluck-entry-loading"
            aria-busy="true"
          >
            <Loader2 className="h-5 w-5 animate-spin text-brand-orange" aria-hidden="true" />
          </div>
        )}

        {/* Phase L.2 P2 — audit CPE-2 + CPE-3. Polite region announces
            the phase transitions (loading→ok / notfound / error) plus
            the current vote count when it changes. Region stays
            mounted with empty message so the transitions fire. */}
        <PoliteStatus
          visuallyHidden
          message={(() => {
            if (phase === "loading") return "Loading entry…";
            if (phase === "notfound") return "This entry isn't available.";
            if (phase === "error") return "Couldn't load this entry.";
            if (phase === "ok" && entry) {
              return `${entry.dog_name}, ${formatVoteCount(entry.vote_count)}${
                entry.has_voted_by_me ? ", you voted for this entry" : ""
              }.`;
            }
            return "";
          })()}
          testId="cutest-pluck-entry-status"
        />

        {phase === "notfound" && (
          <div
            className="mt-6 rounded-2xl border border-border bg-pp-surface p-10 text-center"
            data-testid="cutest-pluck-entry-notfound"
          >
            <p className="text-base font-semibold text-pp-text">
              This entry isn't available.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              It might be pending review, no longer public, or the link is broken.
            </p>
          </div>
        )}

        {phase === "error" && (
          <div
            className="mt-6 rounded-2xl border border-border bg-pp-surface p-10 text-center"
            data-testid="cutest-pluck-entry-error"
          >
            <p className="text-base font-semibold text-pp-text">
              Couldn't load this entry.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Please try again in a moment.
            </p>
          </div>
        )}

        {phase === "ok" && entry && (
          <>
            <div
              className="mt-6 overflow-hidden rounded-3xl border border-border bg-pp-surface shadow-sm"
              data-testid="cutest-pluck-entry-card"
            >
              <div className="aspect-[4/3] w-full bg-pp-surface-2">
                {entry.photo_url ? (
                  <img
                    src={entry.photo_url}
                    alt={`Photo of ${entry.dog_name}`}
                    className="h-full w-full object-cover"
                    data-testid="cutest-pluck-entry-photo"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                    <ImageOff className="h-8 w-8" aria-hidden="true" />
                  </div>
                )}
              </div>
              <div className="space-y-4 p-6">
                <div className="inline-flex items-center gap-2 rounded-full bg-pp-surface-2 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-orange">
                  <Trophy className="h-3 w-3" aria-hidden="true" />
                  Cutest Pluck 2026
                </div>
                <h1
                  className="font-heading text-4xl font-bold text-pp-text sm:text-5xl"
                  data-testid="cutest-pluck-entry-name"
                >
                  {entry.dog_name}
                </h1>
                {entry.caption && (
                  <p
                    className="text-base text-pp-text"
                    data-testid="cutest-pluck-entry-caption"
                  >
                    {entry.caption}
                  </p>
                )}
                <div
                  className="inline-flex items-center gap-1 text-lg font-semibold text-brand-orange"
                  data-testid="cutest-pluck-entry-count"
                >
                  <Heart className="h-5 w-5" aria-hidden="true" />
                  {formatVoteCount(entry.vote_count)}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {isMyEntry ? (
                    <span
                      className="inline-flex items-center gap-2 rounded-full bg-pp-surface-2 px-4 py-2 text-sm font-semibold text-pp-text"
                      data-testid="cutest-pluck-entry-my-entry"
                    >
                      You're in the running!
                    </span>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleVote}
                      disabled={pending}
                      className={
                        entry.has_voted_by_me
                          ? "rounded-full"
                          : "rounded-full bg-pp-purple text-white hover:bg-pp-purple-dark"
                      }
                      variant={entry.has_voted_by_me ? "secondary" : "default"}
                      data-testid={
                        entry.has_voted_by_me
                          ? "cutest-pluck-entry-voted"
                          : "cutest-pluck-entry-vote"
                      }
                    >
                      {pending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : entry.has_voted_by_me ? (
                        <Check className="mr-2 h-4 w-4" />
                      ) : (
                        <Heart className="mr-2 h-4 w-4" />
                      )}
                      {entry.has_voted_by_me ? "Voted" : "Vote"}
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={handleShare}
                    variant="outline"
                    className="rounded-full"
                    data-testid="cutest-pluck-entry-share"
                    aria-label="Share this entry"
                  >
                    <Share2 className="mr-2 h-4 w-4" aria-hidden="true" />
                    Share
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </PublicPageShell>
  );
}
